import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Calendar, Video } from 'lucide-react';
import { usePermissions } from '@contexts/AuthContext';
import { schedulerService } from '@services';
import PageLayout from '@components/layout/PageLayout';
import Button from '@components/common/Button';
import DetailPageHeader from '@components/common/DetailPageHeader';
import Badge from '@components/common/Badge';
import TierBadge from '@components/common/TierBadge';
import EntityActivity from '@components/common/EntityActivity';
import Tooltip from '@components/common/Tooltip';
import DetailSection from '@components/common/DetailSection';
import { AttachmentSection } from '@components/features/attachments';
import { InfoGrid, InfoItem } from '@components/common/InfoGrid';
import { RZTable } from '@components/common/table';
import type { ScheduleItemContract, AssessmentStatus, TableColumn } from '@app-types';
import { formatCalendarDate, formatShortDate } from '@utils/dateUtils';
import { getMeetingStatusLabel } from '@utils/statusUtils';
import { getAssessmentStatusLabel } from '@utils/referenceCatalog';
import { formatAssessmentId } from '@utils/exportUtils';
import { meetingStatusToVariant, statusToVariant, tierToVariant } from '@utils/badgeMappers';
import { LoadingState } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import styles from './MeetingDetailPage.module.css';

// Column configuration for meeting contracts table
const meetingContractColumns: TableColumn[] = [
  { key: 'contract_number', label: 'Contract Number', defaultVisible: true, alwaysVisible: true },
  { key: 'supplier', label: 'Supplier', defaultVisible: true },
  { key: 'assessment_id', label: 'Assessment #', defaultVisible: true },
  { key: 'assessment_status', label: 'Assessment Status', defaultVisible: true },
  { key: 'contract_owners', label: 'Contract Owner(s)', defaultVisible: true },
  { key: 'division', label: 'Division', defaultVisible: true },
  { key: 'tier', label: 'Tier', defaultVisible: true },
  { key: 'target_date', label: 'Target Date', defaultVisible: true }
];

const MeetingDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const permissions = usePermissions();

  // Fetch meeting details
  const {
    data: meeting,
    isLoading,
    error
  } = useQuery({
    queryKey: ['contract-assessment-meeting', id],
    queryFn: () => schedulerService.getContractAssessmentMeeting(Number(id)),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleContractClick = (contractId: number) => {
    navigate(`/contracts/${contractId}`);
  };

  const handleSupplierClick = (supplierId: number) => {
    navigate(`/suppliers/${supplierId}`);
  };

  const handleAssessmentClick = (assessmentId: number) => {
    navigate(`/assessments/${assessmentId}`);
  };

  // Cell rendering logic for RZTable
  const renderContractCell = (column: TableColumn, contract: ScheduleItemContract) => {
    switch (column.key) {
      case 'contract_number':
        return (
          <button className="table-link" onClick={() => handleContractClick(contract.id)}>
            {contract.contract_number}
          </button>
        );

      case 'supplier':
        return (
          <button className="table-link" onClick={() => handleSupplierClick(contract.supplier_id)}>
            {contract.supplier_name}
          </button>
        );

      case 'assessment_id':
        return contract.assessment?.id ? (
          <button className="table-link" onClick={() => handleAssessmentClick(contract.assessment!.id)}>
            {formatAssessmentId(contract.assessment.id)}
          </button>
        ) : (
          <span className="no-date">Not Created</span>
        );

      case 'assessment_status':
        return contract.assessment?.status ? (
          <Badge variant={statusToVariant(contract.assessment.status as AssessmentStatus)}>
            {getAssessmentStatusLabel(contract.assessment.status)}
          </Badge>
        ) : (
          <span className="no-date">—</span>
        );

      case 'contract_owners':
        return contract.contract_owners?.length > 0 ? (
          contract.contract_owners.map((o) => o.name).join(', ')
        ) : (
          <span className="no-date">Not Assigned</span>
        );

      case 'division':
        return contract.division || <span className="no-date">N/A</span>;

      case 'tier':
        return <TierBadge variant={tierToVariant(contract.tier)}>{contract.tier}</TierBadge>;

      case 'target_date':
        return contract.target_date ? formatCalendarDate(contract.target_date) : <span className="no-date">—</span>;

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <PageLayout>
        <LoadingState message="Loading meeting details..." spinnerSize="large" />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <ErrorState
          title="Meeting Not Found"
          message="The meeting you're looking for could not be found."
          action={
            <Button variant="primary" onClick={handleBack}>
              <ArrowLeft size={16} />
              Go Back
            </Button>
          }
        />
      </PageLayout>
    );
  }

  if (!meeting) {
    return null;
  }

  const meetingStatus = meeting.meeting?.status || 'Not Scheduled';
  const meetingDate = meeting.meeting?.scheduled_datetime;
  const joinUrl = meeting.meeting?.join_url;

  const handleJoinMeeting = () => {
    if (joinUrl) {
      window.open(joinUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <PageLayout>
      <DetailPageHeader
        icon={<Calendar size={24} />}
        title={meeting.id ? `Meeting #${meeting.id}` : 'Meeting'}
        backLabel="Go Back"
        onBack={handleBack}
        actions={
          <Button variant="primary" onClick={handleJoinMeeting} disabled={!joinUrl}>
            <Video size={16} />
            Join Meeting
          </Button>
        }
      />

      <div className={styles.meetingDetailContent}>
        {/* Meeting Overview Section */}
        <DetailSection title="Meeting Information">
          <InfoGrid className={styles.infoGrid}>
            <InfoItem label="Meeting Date">
              {meetingDate ? formatShortDate(meetingDate) : <span className="no-date">Not Scheduled</span>}
            </InfoItem>
            <InfoItem label="Status">
              <Badge variant={meetingStatusToVariant(meetingStatus)}>
                {getMeetingStatusLabel(meetingStatus)}
              </Badge>
            </InfoItem>
            <InfoItem label="Scheduled By">
              {meeting.processed_by?.name || <span className="no-date">N/A</span>}
            </InfoItem>
            <InfoItem label="Assessor">
              <div className={styles.infoValueWithBadge}>
                {meeting.assessor?.name ? (
                  <span>{meeting.assessor.name}</span>
                ) : (
                  <span className="no-date">Not Assigned</span>
                )}
                {meeting.meeting?.invites && meeting.assessor?.id && (
                  <InvitationStatus invites={meeting.meeting.invites} userId={meeting.assessor.id} />
                )}
              </div>
            </InfoItem>
            {meeting.contracts[0]?.contract_managers && meeting.contracts[0].contract_managers.length > 0 && (
              <InfoItem label={`Contract Manager${meeting.contracts[0].contract_managers.length > 1 ? 's' : ''}`}>
                <div className={styles.infoValueList}>
                  {meeting.contracts[0].contract_managers.map((manager) => (
                    <div className={styles.infoValueWithBadge} key={`manager-${manager.id}`}>
                      <span>{manager.name}</span>
                      {meeting.meeting?.invites && (
                        <InvitationStatus invites={meeting.meeting.invites} userId={manager.id} />
                      )}
                    </div>
                  ))}
                </div>
              </InfoItem>
            )}
          </InfoGrid>
        </DetailSection>

        {/* Contracts Section */}
        <DetailSection title={`Contracts in Meeting (${meeting.contracts.length})`}>
          <RZTable
            data={meeting.contracts}
            columns={meetingContractColumns}
            getRowKey={(contract) => contract.id}
            renderCell={renderContractCell}
            state={{
              isLoading: false,
              isEmpty: meeting.contracts.length === 0,
              emptyTitle: 'No contracts in meeting',
              emptyDescription: 'This meeting has no contracts assigned.',
              emptyType: 'contracts'
            }}
          />
        </DetailSection>

        {meeting.id && (
          <AttachmentSection
            entityType="meeting"
            entityId={meeting.id}
            canManage={permissions.isAdmin || permissions.isContractRole || permissions.isScheduler || permissions.isAssessor}
          />
        )}

        {/* Meeting Activity Section */}
        {meeting.id && permissions.canAccessNotes && (
          <EntityActivity entityType="meeting" entityId={meeting.id} title="Meeting Activity" />
        )}
      </div>
    </PageLayout>
  );
};

// Helper component for invitation status icon (same style as MeetingTooltip)
const InvitationStatus: React.FC<{
  invites: Array<{ user_id: number; status: string }>;
  userId?: number;
}> = ({ invites, userId }) => {
  const invite = userId ? invites.find((i) => i.user_id === userId) : null;
  const status = invite?.status || 'sent';

  if (!userId || !invite) {
    return null;
  }

  const getStatusIcon = () => {
    switch (status) {
      case 'accepted':
        return '✓';
      case 'declined':
        return '✕';
      case 'sent':
      default:
        return '?';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'accepted':
        return 'Accepted';
      case 'declined':
        return 'Declined';
      case 'sent':
        return 'Pending';
      default:
        return 'Unknown';
    }
  };

  const statusIconMap: Record<string, string> = {
    accepted: styles.invitationStatusIconAccepted,
    declined: styles.invitationStatusIconDeclined,
  };
  const iconClass = statusIconMap[status] ?? styles.invitationStatusIconSent;

  return (
    <Tooltip content={getStatusLabel()} position="bottom" delay={0}>
      <span className={`${styles.invitationStatusIcon} ${iconClass}`}>{getStatusIcon()}</span>
    </Tooltip>
  );
};

export default MeetingDetailPage;
